import type { ThemeConfig } from './theme';
import { applyTransformOnKeys } from '../utils/transform';

export const spacingProperties = [
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginX',
  'marginY',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'paddingX',
  'paddingY',
] as const;

export type SpacingProperty = (typeof spacingProperties)[number];

export const resolveSpacing = (theme: ThemeConfig) =>
  function _resolve(factor: number | string): string {
    /*
     * This is catching undefined and null just as a safeguard since
     * we are still using some //@ts-ignore while we fix typing
     */
    if (factor === undefined || factor === null || factor === '') {
      return '';
    }
    /*
     * If factor is of type number, we can just convert it to px by
     * multiplying it by our theme spacing unit.
     */
    if (typeof factor === 'number') {
      if (factor === 0) {
        return '0';
      }
      return `${theme.spacing * factor}px`;
    }
    /*
     * Since we want to support shorthand properties, we check if any
     * string value contains more than one value if it does, we recurse.
     */
    const factorArray = factor.split(' ');
    if (factorArray.length > 1) {
      return factorArray.map(_resolve).join(' ');
    }
    /*
     * If it is a single value, we will check if it has any
     * units or the parsedFloat is a number.
     * We recurse over the value if it is a valid number, otherwise
     * we simply leave the value intact.
     */
    const parsedValue = +factorArray[0];

    return Number.isNaN(parsedValue) ? factorArray[0] : _resolve(parsedValue);
  };

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const resolveSpacingProperties = (theme: ThemeConfig) =>
  applyTransformOnKeys(spacingProperties, resolveSpacing(theme));
